/* eslint-disable no-restricted-globals */

import axios from "axios";
import pDebounce from "p-debounce";
import defaultsDeep from "lodash.defaultsdeep";
import figlet from "figlet";
import standard from "figlet/importable-fonts/Standard.js";
import pJson from "../../../package.json";
import { db, refManager } from "../../db";
import { socket } from "../../transport";
import { queryClient } from "../../App";
import { setDataStatus } from "./dataState";
import { translateTokens } from "./fingerprintTokens";
import {
  executeBatch,
  conflictError,
  batchKeys,
  invalidConfigText,
  modificheDeletes
} from "./batch";
import { shouldApplyUpdate } from "./socketEvents";
import {
  createReloadOverlay,
  failedFetch,
  isFailedFetch,
  keepsLoadedTable
} from "./reload";
import draft from "../draft/instance";
import { applyRows, computeLabelsColors } from "./applyRows";

// https://github.com/pmmmwh/react-refresh-webpack-plugin/issues/24#issuecomment-672816401
// self.$RefreshReg$ = () => {};
// self.$RefreshSig$$ = () => () => {};

const apiEndp = process.env.REACT_APP_API_ENDPOINT;
const deserializeMap = (map) => new Map(JSON.parse(map));
const debounceWait = 3000;

const debouncedReloadVolatile = pDebounce((tenant, branch, id_token) => {
  return updateVolatileRequestMetadata(tenant, branch, id_token);
}, debounceWait);

const rejectSave = (data, error, options) => {
  const { accessor, Id } = options;
  data.error = error;
  data.accessor = accessor;
  data.Id = Id;

  saving = false;

  return JSON.stringify(data);
};

let TENANT;
let BRANCH;
let TOKEN;

// The rows received while a reload fetches its tables (see reload.js)
const reloadOverlay = createReloadOverlay();

export let startRequested = false;
export let started = false;
export let loadingData = false;
export let saving = false;
export let healthCheck;

function booleanToIndex(data) {
  Object.values(data).forEach((elm) => {
    Object.entries(elm).forEach(([key, value]) => {
      if (value === true) elm[key] = 1;
      else if (value === false) elm[key] = 0;
    });
  });
  return data;
}

function computeKeys(keys) {
  const ret = [];

  Object.entries(keys).map(([id, key]) => ret.push({ id, key }));

  return ret;
}

function socketRequest(key, payload) {
  return new Promise((resolve, reject) => {
    console.time(`🤟🏻 REQUEST-${key.toUpperCase()}`);
    socket.emit(key, payload, (data) => {
      if (data.data) resolve(data.data);
      else reject(data.error);
      console.timeEnd(`🤟🏻 REQUEST-${key.toUpperCase()}`);
    });
  });
}

function requestData(tenant, branch, id_token, what) {
  return new Promise((resolve, reject) => {
    console.time(`🤟🏻 REQUEST-${what.toUpperCase()} [${branch}]`);
    socket.emit(
      "data",
      { what, tenant, branch, "id-token": id_token },
      (data) => {
      if (data.data) {
        if (what === "keys") resolve(computeKeys(data.data));
        if (what === "labels") resolve(computeLabelsColors(data.data));
        console.timeEnd(`🤟🏻 REQUEST-${what.toUpperCase()} [${branch}]`);
        resolve(booleanToIndex(data.data));
      } else {
        if (data.error) console.error(`${what.toUpperCase()} ${data.error}`);
        else console.warn(`⚠️ NO DATA FOR ${what.toUpperCase()} [${branch}]`);
        // Empty, marked as failed: it does not replace a loaded table
        resolve(failedFetch());
        console.timeEnd(`🤟🏻 REQUEST-${what.toUpperCase()} [${branch}]`);
      }
    });
  });
}

function requestHealthData(tenant) {
  return new Promise((resolve, reject) => {
    socket.emit("health", false, (data) => {
      resolve(data);
    });
  });
}

function requestSettings(tenant, branch) {
  const defaults = require("../../settings/client.json");

  return new Promise((resolve, reject) =>
    axios
      .get(`${apiEndp}/settings/${tenant}`, {
        headers: { ctx: `branch=${branch}` }
      })
      .then(({ data }) => {
        if (data) {
          resolve(defaultsDeep(data, defaults));
        }
      })
      .catch((e) => reject(e))
  );
}

function requestNotes(tenant, branch) {
  return new Promise((resolve, reject) =>
    axios
      .get(`${apiEndp}/notes/${tenant}/all`, {
        headers: { ctx: `branch=${branch}` }
      })
      .then(({ data }) => {
        if (data) resolve(data);
      })
      .catch((e) => reject(e))
  );
}

function deleteNote({ tenant, id_token, code, index, branch }) {
  return new Promise((resolve, reject) =>
    axios
      .delete(`${apiEndp}/notes/${tenant}/${code}/${index}`, {
        headers: { "id-token": id_token, ctx: `branch=${branch}` }
      })
      .then(({ data }) => {
        if (data) {
          db.deleteTable("notes");
          db.bulkPut("notes", data);
          db.put("aresInternals", {
            id: "notesFetch",
            lastUpdate: Date.now(),
            length: Object.keys(data)?.length
          });

          resolve(data);
        }
      })
      .catch((e) => reject(e))
  );
}

function requestHelp() {
  return new Promise((resolve, reject) =>
    axios
      .get(`${apiEndp}/help/all`)
      .then(({ data }) => {
        if (data) resolve(data);
      })
      .catch((e) => reject(e))
  );
}

function requestMetadata(tenant, branch) {
  return new Promise((resolve, reject) =>
    axios
      .get(`${apiEndp}/metadata/all/${tenant}`, {
        headers: { ctx: `branch=${branch}` }
      })
      .then(({ data }) => {
        if (data) resolve(data);
      })
      .catch((e) => reject(e))
  );
}

function requestPolicies(tenant, id_token) {
  return new Promise((resolve, reject) => {
    axios
      .get(`${apiEndp}/users/policies/${tenant}`, {
        headers: { "id-token": id_token }
      })
      .then(({ data }) => {
        if (data) resolve(data);
      })
      .catch((e) => reject(e));
  });
}

function requestUsers(tenant, id_token) {
  return new Promise((resolve, reject) => {
    axios
      .get(`${apiEndp}/users/${tenant}`, {
        headers: { "id-token": id_token }
      })
      .then(({ data }) => {
        if (data) resolve(data);
      })
      .catch((e) => reject(e));
  });
}

// The rows of the response of our own write (default) or of a colleague's
// update-data event (colleague: true), applied by applyRows. A row journaled
// in a draft is not applied: the draft stays visible, and a colleague's newer
// version becomes a conflict of its scope; nor is the row of an open form
// that has changes (the hold): see draft.incoming. sent: the rows our
// request sent, with their draft revision at that time (draft.revsOf).
async function refreshData(
  payload,
  { colleague = false, sent, username } = {}
) {
  const map = deserializeMap(payload.data2Refresh);
  const keys = Array.from(map.keys())
    .map((k) => k.toUpperCase())
    .join(" + ");

  console.time(`⚡️ REFRESH:[${keys}]`);

  applyRows(
    map,
    { db, refManager, draft },
    {
      colleague,
      sent,
      username,
      // A reload running now fetched this table before this row, maybe
      record: reloadOverlay.record
    }
  );

  console.timeEnd(`⚡️ REFRESH:[${keys}]`);
  refManager.checkQueriesHealth();
}

export async function updateData(tenant, branch, id_token) {
  return new Promise(async (resolve, reject) => {
    loadingData = true;
    const standardTables = [
      "analitica",
      "params",
      "properties",
      "endpoints",
      "blocks",
      "queries",
      "rules",
      "rulesPresets",
      "decorators",
      "lists",
      "externalSources",
      "responseFields",
      "ui",
      "keys",
      "services",
      "enrichers",
      "conversions",
      "experiments",
      "conversionPacks",
      "contentPacks",
      "metaPacks",
      "pipelines",
      "loaders",
      "personas"
    ];
    const allTables = standardTables.concat(["labels", "labelsAll"]);

    const reload = reloadOverlay.begin();
    try {
      const data = new Map(
        await requestData(tenant, branch, id_token, "labels")
      );
      for (const dataType of standardTables) {
        data.set(
          dataType,
          await requestData(tenant, branch, id_token, dataType)
        );
      }

      // ****************
      // MEMORY DB
      // ***************
      const reloaded = new Set();
      for (const dataType of allTables) {
        console.time(`⚡️ MEM-UPDATE-${dataType.toUpperCase()}`);
        const refresh = data.get(dataType);
        const isLabels = dataType === "labels" || dataType === "labelsAll";
        const failed = isLabels ? !data.has(dataType) : isFailedFetch(refresh);
        const loaded = db.getTableRef(dataType);

        // A failed fetch does not empty a table loaded before: its rows (and
        // the drafts in them) stay as they are
        if (keepsLoadedTable(failed, loaded)) {
          data.set(dataType, [...loaded.values()]);
          console.timeEnd(`⚡️ MEM-UPDATE-${dataType.toUpperCase()}`);
          continue;
        }

        let obj = Array.isArray(refresh)
          ? refresh
          : Object.values(refresh ?? {});
        // The rows received while the tables were fetched (our saves, a
        // colleague's) are newer than the table fetched before them
        if (!isLabels) {
          obj = reloadOverlay.patch(dataType, obj);
          data.set(dataType, obj);
        }

        db.deleteTable(dataType);
        db.bulkPut(dataType, obj);
        reloaded.add(dataType);
        console.timeEnd(`⚡️ MEM-UPDATE-${dataType.toUpperCase()}`);
      }

      refManager.reset();
      refManager.fillBelongs(data);
      // The drafts back into the tables just loaded (same tick: no render
      // sees the stored rows in between)
      draft.reapply({ tables: reloaded });
    } catch (error) {
      loadingData = false;
      reject(error);
      return;
    } finally {
      reloadOverlay.end(reload);
    }

    loadingData = false;
    db.put("aresInternals", {
      id: "dataFetch",
      lastUpdate: Date.now(),
      realms: allTables
    });
    resolve(allTables);
  });
}

export async function updateHealthData(tenant) {
  loadingData = true;
  const health = await requestHealthData(tenant);
  loadingData = false;
  db.put("health", health);
  db.put("aresInternals", {
    id: "healthFetch",
    lastUpdate: Date.now(),
    regressions: health?.regressions?.isCritical,
    versions: health?.versions?.isCritical
  });
}

export async function updatePolicies(tenant, id_token) {
  loadingData = true;
  const policies = await requestPolicies(tenant, id_token);
  loadingData = false;
  db.put("policies", { ...policies, id: "p1" });
  db.put("aresInternals", {
    id: "policiesFetch",
    lastUpdate: Date.now(),
    tenants: policies?.tenants?.length,
    groups: Object.keys(policies?.groups || {}).length,
    permissions: Object.keys(policies?.permissions || {}).length,
    roles: Object.keys(policies?.roles || {}).length
  });
}

export async function updateSettings(tenant, branch) {
  loadingData = true;
  const settings = await requestSettings(tenant, branch);
  loadingData = false;
  db.put("settings", settings);
  db.put("aresInternals", {
    id: "settingsFetch",
    lastUpdate: Date.now()
  });
}

export async function updateHelp(tenant) {
  loadingData = true;
  const help = await requestHelp(tenant);
  loadingData = false;

  db.deleteTable("help");
  db.bulkPut("help", help);
  db.put("aresInternals", {
    id: "helpFetch",
    lastUpdate: Date.now(),
    length: help.length
  });
}

export async function updateNotes(tenant, branch) {
  loadingData = true;
  const notes = await requestNotes(tenant, branch);
  loadingData = false;

  db.deleteTable("notes");
  db.bulkPut("notes", notes);
  db.put("aresInternals", {
    id: "notesFetch",
    lastUpdate: Date.now(),
    length: notes.length
  });
}

export async function updateUsers(tenant, id_token) {
  loadingData = true;
  const users = await requestUsers(tenant, id_token);
  loadingData = false;

  db.deleteTable("users");
  db.bulkPut("users", users);
  db.put("aresInternals", {
    id: "usersFetch",
    lastUpdate: Date.now(),
    userLength: users.length
  });
}

export async function updateMetadata(tenant, branch) {
  loadingData = true;
  console.time("🌼 MEM-UPDATE-METADATA");
  const metadata = await requestMetadata(tenant, branch);

  db.delete("metadata");
  db.bulkPut("metadata", metadata);
  db.put("aresInternals", {
    id: "metadataFetch",
    lastUpdate: Date.now(),
    metadataLength: metadata.length
  });

  console.timeEnd("🌼 MEM-UPDATE-METADATA");
  loadingData = false;
}

export async function updateVolatileRequestMetadata(tenant, branch, id_token) {
  return new Promise((resolve, reject) => {
    loadingData = true;
    console.time("🦅 MEM-UPDATE-VOLATILE-METADATA");
    axios
      .get(`${apiEndp}/metadata/request/volatile/${tenant}`, {
        headers: { "id-token": id_token, ctx: `branch=${branch}` }
      })
      .then(({ data }) => {
        if (data) {
          db.deleteByQuery("metadata", (i) => i.volatile);
          db.bulkPut("metadata", data);
          db.put("aresInternals", {
            id: "metadataVolatile",
            lastUpdate: Date.now(),
            length
          });
          loadingData = false;
          resolve(data);
          console.timeEnd("🦅 MEM-UPDATE-VOLATILE-METADATA");
        } else {
          loadingData = true;
          reject(data.error);
          console.timeEnd("🦅 MEM-UPDATE-VOLATILE-METADATA");
        }
      })
      .catch((e) => {
        reject(e);
        console.timeEnd("🦅 MEM-UPDATE-VOLATILE-METADATA");
      });
  });
}

// The tenant and branch the worker has loaded now (start). A write
// captures them when it leaves: its response after a change of them (the
// worker restarted on another branch) is of the data left, and refreshes
// nothing (the draft store refuses its rows too: revsOf).
const loadedNow = () => `${TENANT}|${BRANCH}`;

// The server answered a write with a refusal (a 409, another 4xx): it
// stored nothing. No answer, or a gateway's (5xx): the write may have gone
// through.
const refusedByServer = (error) => {
  const status = Number(error?.response?.status);
  return status >= 400 && status < 500;
};

export async function saveDataItem(tenant, id_token, options = {}, onWarnings) {
  const {
    accessor,
    Id,
    prevId,
    isNew,
    data,
    branch,
    skipdeps = false,
    baseVersion
  } = options;
  const sent = draft.revsOf(batchKeys({ items: [options] }));
  // The row this save stores is ours by whatever way it comes back (a full
  // reload, a fetch, the socket), also before its response or without it
  const expected = draft.expectSaved([options], sent);
  const loaded = loadedNow();

  return new Promise((resolve, reject) => {
    saving = true;
    axios
      .put(
        `${apiEndp}/item/${accessor}/${tenant}`,
        {
          Id,
          prevId,
          isNew,
          data,
          // Version check (409 when the stored item has another version)
          ...(baseVersion !== undefined && { baseVersion })
        },
        {
          headers: {
            "id-token": id_token,
            skipdeps,
            "caller-id": socket.id,
            ctx: `branch=${branch}`
          }
        }
      )
      .then(({ data: result }) => {
        saving = false;
        if (result.status === "OK") {
          onWarnings?.(result.warnings);
          resolve(Id);
          if (loadedNow() !== loaded) return;
          refreshData(result, { sent }).catch((error) =>
            reject(rejectSave(data, error, options))
          );
          debouncedReloadVolatile(tenant, branch, id_token);
          return;
        }
        draft.unexpectSaved(expected);
        if (result.status === "CONFLICT") reject(conflictError(result));
        else
          reject(
            rejectSave(
              data,
              invalidConfigText(result) ?? result.error,
              options
            )
          );
      })
      .catch((error) => {
        saving = false;
        if (refusedByServer(error)) draft.unexpectSaved(expected);
        if (error?.response?.status === 409)
          reject(conflictError(error.response.data));
        else
          reject(
            rejectSave(
              data,
              invalidConfigText(error?.response?.data) ?? error.message,
              options
            )
          );
      });
  });
}

export async function deleteDataItem(tenant, id_token, options = {}) {
  const { accessor, Id, reference, branch, baseVersion } = options;
  const sent = draft.revsOf(batchKeys({ deletes: [options] }));
  const loaded = loadedNow();

  return new Promise((resolve, reject) => {
    axios
      .delete(`${apiEndp}/item/${accessor}/${tenant}/${Id}`, {
        headers: {
          "id-token": id_token,
          reference,
          "caller-id": socket.id,
          ctx: `branch=${branch}`,
          // Version check (409 when the stored item has another version)
          ...(baseVersion !== undefined && { basever: baseVersion })
        }
      })
      .then(({ data }) => {
        if (data.status === "OK") {
          resolve(Id);
          if (loadedNow() !== loaded) return;
          refreshData(data, { sent }).catch((error) =>
            reject(JSON.stringify({ ...options, error }))
          );
          debouncedReloadVolatile(tenant, branch, id_token);
        } else if (data.status === "CONFLICT") reject(conflictError(data));
        else reject(JSON.stringify({ ...options, error: data.status }));
      })
      .catch((e) => {
        if (e?.response?.status === 409) reject(conflictError(e.response.data));
        else reject(JSON.stringify({ ...options, error: e.message }));
      });
  });
}

export async function saveModifiche(tenant, id_token, modifiche = {}) {
  // The rows its modifiche delete are our own write (see modificheDeletes)
  const sent = draft.revsOf(modificheDeletes(modifiche));
  const loaded = loadedNow();

  return new Promise((resolve, reject) => {
    axios
      .put(
        `${apiEndp}/modifiche/${tenant}`,
        { modifiche },
        {
          headers: {
            "id-token": id_token,
            "caller-id": socket.id,
            ctx: `branch=${modifiche.branch}`
          }
        }
      )
      .then(({ data: result }) => {
        if (result.status === "OK") {
          resolve("OK", { modifiche });
          if (loadedNow() !== loaded) return;
          refreshData(result, { sent }).catch((error) => reject(error));
        } else {
          reject(result.error);
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
}
// Items, deletes and modifiche of one operation in one request (one commit on
// the backend). A backend without PUT /items (404) gets the calls of before,
// one after the other (a delete with its baseVersion as the basever header).
// The response of each is applied (refreshData: applyRows, nothing awaited
// before it) before the next call is made: when a later call fails, the
// rows of the calls that went through are applied already (the delete of a
// property from its list tells "deleted, but not its pages" by it).
export async function saveBatch(tenant, id_token, options = {}, onWarnings) {
  const { branch } = options;
  const sent = draft.revsOf([
    ...batchKeys(options),
    ...modificheDeletes(options.modifiche)
  ]);
  // The rows this save stores are ours by whatever way they come back (see
  // saveDataItem); refused: the server answered, nothing stored. Without
  // PUT /items (404) the calls of before expect their own rows
  // (saveDataItem): the ones of this batch they did not replace were never
  // sent.
  const expected = draft.expectSaved(options.items ?? [], sent);
  const loaded = loadedNow();
  let refused = false;

  saving = true;
  try {
    const { result, results } = await executeBatch(options, {
      request: (body) =>
        axios
          .put(`${apiEndp}/items/${tenant}`, body, {
            headers: {
              "id-token": id_token,
              "caller-id": socket.id,
              ctx: `branch=${branch}`
            }
          })
          .then(
            ({ data }) => {
              if (data?.status !== "OK") refused = true;
              return data;
            },
            (error) => {
              // 404: no PUT /items, the calls of before (the fallback):
              // nothing of this request stored either
              if (refusedByServer(error)) refused = true;
              throw error;
            }
          ),
      fallback: {
        saveDataItem: (item) => saveDataItem(tenant, id_token, item, onWarnings),
        deleteDataItem: (item) => deleteDataItem(tenant, id_token, item),
        saveModifiche: (modifiche) =>
          saveModifiche(tenant, id_token, modifiche)
      }
    });

    if (!result) {
      draft.unexpectSaved(expected);
      return results;
    }
    onWarnings?.(result.warnings);
    if (loadedNow() !== loaded) return result;

    refreshData(result, { sent }).catch((error) =>
      console.error("⚠️ BATCH REFRESH", error)
    );
    debouncedReloadVolatile(tenant, branch, id_token);

    return result;
  } catch (error) {
    if (refused) draft.unexpectSaved(expected);
    throw error;
  } finally {
    saving = false;
  }
}

// The stored rows of some items (e.g. the conflicts of a refused save, whose
// response has no data), fetched with their tables and applied to MemoryDB.
// A row that is not stored any more is removed. A drafted row is left to its
// draft (draft.incoming: a conflict of its scope when it changed), a failed
// fetch changes nothing. refs: [{ accessor, id }]. Resolves { failed }: the
// refs whose table could not be fetched.
export async function reloadItems(refs = []) {
  const byTable = new Map();
  for (const { accessor, id } of refs) {
    if (!accessor || id === undefined || accessor === "labels") continue;
    if (!byTable.has(accessor)) byTable.set(accessor, new Set());
    byTable.get(accessor).add(id);
  }

  let removed = false;
  const failed = [];
  const reload = reloadOverlay.begin();
  try {
    for (const [table, ids] of byTable.entries()) {
      const fetched = await requestData(TENANT, BRANCH, TOKEN, table);
      // The request failed: nothing can be said about these rows
      if (isFailedFetch(fetched)) {
        for (const id of ids) failed.push({ accessor: table, id });
        continue;
      }

      // With the rows received while it was fetched (newer)
      const rows = reloadOverlay.patch(
        table,
        Array.isArray(fetched) ? fetched : Object.values(fetched ?? {})
      );
      const found = [];

      for (const id of ids) {
        const row = rows.find((row) => row?.id === id);
        // A drafted row is not overwritten: its draft takes the stored row
        // (a conflict of its scope, or the row it is based on)
        const kept = draft.incoming(
          table,
          { action: row ? "upsert" : "delete", data: row ?? { id } },
          // A fetch: the server serves it now (see OUR OWN SAVES)
          { colleague: true, fetched: true }
        );
        if (kept) continue;

        if (row) found.push(row);
        else {
          refManager.delete(table, id);
          db.delete(table, id);
          removed = true;
        }
      }

      for (const row of found) db.put(table, row);
      if (found.length) refManager.fillBelongs(db.getDB(), [found], table);
    }
  } finally {
    reloadOverlay.end(reload);
  }

  // The rows that depend on a removed one (see refreshData)
  if (removed) refManager.refill();
  return { failed };
}

export async function policiesSave(tenant, id_token, policies) {
  return new Promise((resolve, reject) => {
    axios
      .put(
        `${apiEndp}/users/policies/${tenant}`,
        { policies },
        { headers: { "id-token": id_token, "caller-id": socket.id } }
      )
      .then(({ data }) => {
        resolve(data);
      })
      .catch((e) => {
        reject(e.error);
      });
  });
}

export async function helpSave(tenant, id_token, { code, dx, lang }) {
  return new Promise((resolve, reject) => {
    axios
      .put(
        `${apiEndp}/help/${tenant}/${lang}/${code}`,
        { html: dx },
        { headers: { "id-token": id_token, "caller-id": socket.id } }
      )
      .then(({ data }) => {
        resolve(data);
      })
      .catch((e) => {
        reject(e.error);
      });
  });
}

export async function notesSave(tenant, id_token, { code, dx, branch }) {
  return new Promise((resolve, reject) => {
    axios
      .put(
        `${apiEndp}/notes/${tenant}/${code}`,
        { notes: dx },
        {
          headers: {
            "id-token": id_token,
            "caller-id": socket.id,
            ctx: `branch=${branch}`
          }
        }
      )
      .then(({ data }) => {
        resolve(data);
      })
      .catch((e) => {
        reject(e.error);
      });
  });
}

export async function userSave(tenant, id_token, body) {
  return new Promise((resolve, reject) => {
    axios
      .put(`${apiEndp}/users/${tenant}`, body, {
        headers: { "id-token": id_token, "caller-id": socket.id }
      })
      .then(({ data }) => {
        if (data.status === "KO") {
          reject(data.error);
        } else {
          resolve(data);
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export async function userDelete(tenant, id_token, { user_id, nickname }) {
  return new Promise((resolve, reject) => {
    axios
      .delete(`${apiEndp}/users/${tenant}/${user_id}/${nickname}`, {
        headers: { "id-token": id_token, "caller-id": socket.id }
      })
      .then(({ data }) => {
        if ((data || {}).status === "KO") {
          reject(data.error);
        } else {
          resolve(data);
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export async function translateFingerprints(fingerprints) {
  // I token "clean" (key=value del codec) sono decodificati in locale; solo gli
  // md5 legacy passano dalla tabella Redis md5 -> "key≠value" esposta dall'API.
  return translateTokens(fingerprints, async (tokens) => {
    const { data } = await axios.post(
      `${apiEndp}/fingerprints/kv/${TENANT}`,
      { fingerprints: tokens },
      { headers: { "id-token": TOKEN, ctx: `branch=${BRANCH}` } }
    );
    return data;
  });
}

export function getParams() {
  const settings = db.get("ui", "server");
  return db.get("params", settings.paramsId);
}

export async function getUserFingerprintsFields(metadata) {
  const result = new Map();
  const params = getParams();

  const fields = [
    ...new Set(
      Object.values(params.users.clusters.types)
        .map((user) => {
          const fields = new Set();
          Object.values(user.events).forEach((event) => {
            Object.values(event.contentTypes).forEach((type) => {
              Object.values(type.fields.fingerprint).forEach((field) =>
                fields.add(field)
              );
            });
          });

          return [...fields];
        })
        .flat()
    )
  ];

  for (const field of fields) {
    if (metadata[field]) {
      const translation = [];

      for (const value of (
        await translateFingerprints(metadata[field].split(" "))
      ).values()) {
        translation.push(value);
      }

      result.set(field, translation);
    }
  }

  return result.size ? result : null;
}

export async function getUserHistoryContent({
  blockId,
  blockIndex,
  placeholders,
  metadata
}) {
  const block = db.get("blocks", blockId);
  const catalog = block.blocks[blockIndex].results.contentType;
  const responseFields = (
    await db.get(
      "responseFields",
      block.blocks[blockIndex].results.responseFieldsId
    )
  ).fields;
  const params = getParams();
  const index = params.content.types[catalog].index;

  const fields = [
    ...new Set(
      Object.values(params.users.clusters.types)
        .map((user) => {
          const fields = new Set();
          Object.values(user.events).forEach((event) => {
            const campi = event.contentTypes[catalog].fields;
            fields.add(campi.ordered || campi.simple);
          });

          return [...fields];
        })
        .flat()
    )
  ];

  const keys = Object.entries(placeholders ?? {}).map(([key, value]) => [
    key.replace(/#|{|}/g, ""),
    value
  ]);

  const resultsMap = [];

  for (const field of fields) {
    const meta = metadata[field];

    if (meta) {
      const [ids, tsMap] = (() => {
        const ids = [];
        const tsMap = new Map();

        try {
          let data;
          try {
            data = JSON.parse(`[${meta}]`);
          } catch (error) {
            data = meta.split(",");
          }

          data.sort(function compare(a, b) {
            if (!a.ts) return -1;

            var dateA = new Date(a.ts);
            var dateB = new Date(b.ts);
            return dateA - dateB;
          });

          data.forEach((d) => {
            if (d.id) {
              tsMap.set(d.id, d.ts);
              ids.push(d.id);
            } else ids.push(d);
          });
        } catch (error) {
          console.error("⚠️ User Data Parse Error", meta, field, error);
        }

        return [ids, tsMap];
      })();

      const results = await socketRequest("contentData", {
        _source: responseFields,
        index,
        ids
      });

      const mapped = results.map((r) => {
        const c = {};
        keys.forEach(([key, value]) => {
          c[key] = r[value];

          if (key === "contentId") {
            c.ts = tsMap.get(r[value]);
          }
        });

        return c;
      });

      resultsMap.push([field, mapped]);
    }
  }

  return resultsMap;
}

export async function start({ tenant, branch, username, id_token, dispatch }) {
  if (startRequested) return;
  dispatch(setDataStatus("loading", true));
  startRequested = true;

  TENANT = tenant;
  BRANCH = branch;
  TOKEN = id_token;
  // The drafts of another tenant or branch are dropped (a restart of the
  // same one keeps them), with the rows received during a reload of it
  if (draft.bind(tenant, branch)) reloadOverlay.reset();

  // const endP = process.env.REACT_APP_API_ENDPOINT;

  // const evtSource = new EventSource(`${endP}/configs/stream/${TENANT}`);
  // evtSource.onmessage = (event) => {
  //   console.log("event");
  //   console.log(event.data);
  // };

  // evtSource.addEventListener("reload-config", (event) => {
  //   console.log("start");
  //   console.log(event.data);
  // });

  // console.log(evtSource);
  console.clear();
  const environnement = process.env["NODE_ENV"];
  figlet.parseFont("Standard", standard);
  figlet.text(
    "AR3S",
    {
      font: "Standard",
      horizontalLayout: "fitted"
    },
    function (err, data) {
      console.log(data);
      console.log(
        `Control Center v${pJson.version} || DB: ⚡️ MemoryDB 1.0 || ENV: ${environnement}`
      );
    }
  );

  // branch: the one loaded by this start (a change of branch restarts it)
  socket.on("update-data", async (data) => {
    if (!shouldApplyUpdate(data, { tenant, branch, socketId: socket.id }))
      return;
    dispatch(setDataStatus("loading", true));
    refreshData(data, { colleague: true, username: data.username }).then(() => {
      queryClient.refetchQueries({
        queryKey: [`diff-${tenant}-${branch}`],
        type: "active",
        exact: true
      });
      dispatch(setDataStatus("loading", false));
    });
  });

  //  dispatch(setDataStatus("loading", true));

  socket.on("update-request-volatile-metadata", async (data) => {
    if (data.tenant !== tenant) return;
    //await debouncedReloadVolatile(data.tenant, branch, id_token);
    // !!!! RESTART THE WORKER TO UPDATE THE METADATA

    dispatch(setDataStatus("loading", true));

    restart({ tenant, branch, username, id_token, dispatch }).then(() => {
      queryClient.refetchQueries({
        queryKey: [`diff-${tenant}-${branch}`],
        type: "active",
        exact: true
      });
      dispatch(setDataStatus("loading", false));
    });
  });

  socket.on("update-users", async (data) => {
    await updateUsers(tenant, id_token);
  });

  const HEALTH_INTERVAL = 180000;
  healthCheck = setInterval(
    async () => await updateHealthData(tenant),
    HEALTH_INTERVAL
  );
  console.log(`💗 HEALTH CHECK EVERY ${HEALTH_INTERVAL}ms`);

  await Promise.all([
    updateData(tenant, branch, id_token),
    updateMetadata(tenant, branch),
    updateVolatileRequestMetadata(tenant, branch, id_token),
    updateSettings(tenant, branch),
    updateHealthData(tenant),
    updatePolicies(tenant, id_token),
    updateUsers(tenant, id_token),
    updateHelp(tenant),
    updateNotes(tenant, branch)
  ]).then(() => {
    refManager.checkQueriesHealth();
    db.dispatchAll();
    dispatch(setDataStatus("loading", false));
    started = true;
  });

  return true;
}

export async function restart({
  tenant,
  branch,
  username,
  id_token,
  dispatch
}) {
  stop();
  await start({ tenant, branch, username, id_token, dispatch });
}

export function stop() {
  startRequested = false;
  started = false;

  clearInterval(healthCheck);

  socket.removeAllListeners("update-data");
  socket.removeAllListeners("update-request-volatile-metadata");
  socket.removeAllListeners("update-users");
}

export function disconnect() {
  if (socket) socket.disconnect();
}

const worker = {
  start,
  restart,
  stop,
  disconnect,
  getUserHistoryContent,
  getUserFingerprintsFields,
  getParams,
  translateFingerprints,
  userDelete,
  userSave,
  notesSave,
  deleteNote,
  helpSave,
  policiesSave,
  saveModifiche,
  saveBatch,
  reloadItems,
  deleteDataItem,
  saveDataItem,
  updateVolatileRequestMetadata,
  updateMetadata,
  updateUsers,
  updateNotes,
  updateHelp,
  updateSettings,
  updatePolicies,
  updateHealthData,
  updateData,
  refreshData
};

export default worker;
